/**
 * withDialogAction decorator
 *
 * Integrates Dialog components with the ActionsContainer/ActionsMenu system.
 * When wrapped with this decorator, Dialog components will automatically:
 *
 * - Register as actions using their `title` prop as the action label
 * - Show as buttons in the Bar component's right section
 * - Handle open/close state automatically via isOpen/onOpenChange
 * - Support both single dialog (shows button with dialog title) and multiple dialogs (shows "Actions" menu)
 * - Pass onSuccess callback to content component for auto-closing after successful submission
 *
 * Usage:
 * ```jsx
 * <Dialog
 *   content={<SomeForm />}
 *   title="My Dialog"
 *   heightContainer="large"
 * >
 *   <div>Trigger content</div>
 * </Dialog>
 * ```
 *
 * Note: Unlike Modal, Dialog components don't require submission handling - they just open/close.
 * The content component can accept an `onSuccess` prop that will be called to close the dialog.
 */

import {
  useState,
  useCallback,
  forwardRef,
  ComponentType,
  ReactNode,
  ReactElement,
  cloneElement,
  isValidElement,
} from 'react'

import { getComponentName } from 'peach/helpers'
import { useStableCallback } from 'peach/hooks'

import { useClickAction } from './withActionTrigger'

type UseDialogActionProps = {
  label: ReactNode
  disabled?: boolean
}

type DialogActionReturn = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const useDialogAction = ({
  label,
  disabled,
}: UseDialogActionProps): DialogActionReturn => {
  const [isOpen, setIsOpen] = useState(false)

  const onOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const onOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  const $onOpen = useStableCallback(onOpen)

  useClickAction({ label, onClick: $onOpen, disabled })

  return { isOpen, onOpenChange }
}

type DialogProps = {
  title?: ReactNode
  disabled?: boolean
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  content?: ReactNode
}

const withDialogAction = <P extends DialogProps>(
  Component: ComponentType<P>,
) => {
  const displayName = getComponentName(Component, 'withDialogAction')

  const WithDialogAction = forwardRef<any, P>((props, ref) => {
    const { title, disabled, content, ...dialogProps } = props

    const label = title ?? null

    const { isOpen, onOpenChange } = useDialogAction({
      label,
      disabled,
    })

    // Create onSuccess callback that closes the dialog
    const onSuccess = useCallback(() => {
      onOpenChange(false)
    }, [onOpenChange])

    // Clone the content element and pass onSuccess prop if it's a React element
    const enhancedContent = isValidElement(content)
      ? cloneElement(content as ReactElement, { onSuccess })
      : content

    // Pass all original props plus the dialog state and enhanced content
    const renderProps = {
      ref,
      ...dialogProps,
      title,
      isOpen,
      onOpenChange,
      content: enhancedContent,
    } as unknown as P

    return <Component {...renderProps} />
  })

  WithDialogAction.displayName = displayName

  return WithDialogAction
}

export default withDialogAction
